import { useMutation, useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { toast } from 'sonner'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { Hint, SectionTitle } from 'shared/components/section'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from 'shared/components/ui/select'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from 'shared/components/ui/sheet'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDate } from 'shared/lib/utils/date'

/**
 * Partner Policies — the "partners and policies" section (spec §12). An
 * administrator writes a partner's Policy as a typed, closed record; each save
 * is a new immutable version; and the settings in force are the backend
 * evaluator's answer, rendered here and never re-derived (ADR-0006, ADR-0040).
 *
 * The page shows one partner's settings in force and its versions as a table; a
 * new version is written in a side panel (nine fields), seeded from the current one.
 */

type PolicySettings = RouterInputs['policies']['Save']['policy']
// The form's own state: TezGo's tariff per kg may be empty while the admin has not
// typed it (a new partner, or a version saved before the tariff) — "Save" waits for it.
type PolicyForm = Omit<PolicySettings, 'tariffPerKg'> & { tariffPerKg: number | null }
type PolicyVersion = RouterOutputs['policies']['ListVersions'][number]
type Effective = RouterOutputs['policies']['EffectivePermissions']

// How the Recipient is identified at the counter (ADR-0031), and by which document.
type Identification = PolicySettings['identification']
const IDENTIFICATIONS: Identification[] = ['by_code', 'by_document']
type DocumentType = NonNullable<PolicySettings['documentType']>
const DOCUMENT_TYPES: DocumentType[] = ['full_name', 'passport', 'pinfl']

type TrustSetting = PolicySettings['trustSetting']
const TRUST_SETTINGS: TrustSetting[] = ['by_boxes', 'per_piece']

// The typed defaults, designed against TezTez (ADR-0006, ADR-0040, ADR-0033):
// taken in at the hub by boxes within 10 %, the Recipient identified by code; an
// unpaid Parcel stays ten days, a paid one never expires (empty), the partner
// answers in 3 days. TezGo's tariff per kg has no default: the admin types it (ADR-0034).
const DEFAULT_SETTINGS: PolicyForm = {
	recipientPays: true,
	trustSetting: 'by_boxes',
	weightTolerancePercent: 10,
	identification: 'by_code',
	documentType: null,
	unpaidStorageDays: 10,
	paidStorageDays: null,
	answerPeriodDays: 3,
	tariffPerKg: null,
}

function settingsFromVersion(version: PolicyVersion): PolicyForm {
	return {
		recipientPays: version.recipientPays,
		trustSetting: version.trustSetting,
		weightTolerancePercent: version.weightTolerancePercent,
		identification: version.identification,
		documentType: version.documentType,
		unpaidStorageDays: version.unpaidStorageDays,
		paidStorageDays: version.paidStorageDays,
		answerPeriodDays: version.answerPeriodDays,
		tariffPerKg: version.tariffPerKg,
	}
}

/** The words of one set of settings — a version's or the ones in force — each by its field. */
function useWords() {
	const { t } = useTranslate()
	return {
		tariff: (tariffPerKg: number | null) =>
			tariffPerKg === null
				? t('policies.tariffNotSet')
				: t('policies.tariffValue', { tariff: tariffPerKg.toLocaleString('ru-RU') }),
		identification: (identification: Identification, documentType: DocumentType | null | undefined) =>
			identification === 'by_document' && documentType
				? t('policies.identificationSummary', {
						identification: t('policies.identification_by_document'),
						document: t(`policies.documentType_${documentType}`),
					})
				: t(`policies.identification_${identification}`),
		// A storage period: its days, or "never expires" when empty (ADR-0033).
		days: (days: number | null) =>
			days === null ? t('policies.neverExpires') : t('policies.daysValue', { days: String(days) }),
		pays: (recipientPays: boolean) => (recipientPays ? t('policies.paysYesShort') : t('policies.paysNoShort')),
		trust: (trust: TrustSetting, tolerance: number) =>
			t('policies.trustSummary', { trust: t(`policies.trust_${trust}`), tolerance: String(tolerance) }),
	}
}

export function Policies() {
	const { t } = useTranslate()
	const [chosen, setChosen] = useState<string | null>(null)
	const [editing, setEditing] = useState(false)

	const partnersQuery = useQuery(trpc.partners.List.queryOptions())
	const partners = partnersQuery.data ?? []
	// The first partner is shown until another is chosen.
	const partnerId = chosen ?? partners[0]?._id ?? null

	return (
		<PageFrame
			title='policies.title'
			actions={
				<Button data-testid='policy-edit' disabled={!partnerId} onClick={() => setEditing(true)}>
					<Plus />
					{t('policies.newVersion')}
				</Button>
			}
		>
			<div className='flex items-center gap-3'>
				<Select value={partnerId ?? undefined} onValueChange={(v) => setChosen(v)}>
					<SelectTrigger className='w-72' data-testid='policy-partner' aria-label={t('policies.partnerPlaceholder')}>
						<SelectValue placeholder={t('policies.partnerPlaceholder')} />
					</SelectTrigger>
					<SelectContent>
						{partners.map((partner) => (
							<SelectItem key={partner._id} value={partner._id}>
								{partner.name} ({partner.prefix})
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{!partners.length && !partnersQuery.isLoading && (
					<span className='text-sm text-muted-foreground'>{t('policies.noPartners')}</span>
				)}
			</div>

			{partnerId && (
				<PartnerPolicy key={partnerId} partnerId={partnerId} editing={editing} onEditingChange={setEditing} />
			)}
		</PageFrame>
	)
}

function PartnerPolicy({
	partnerId,
	editing,
	onEditingChange,
}: {
	partnerId: string
	editing: boolean
	onEditingChange: (open: boolean) => void
}) {
	const { t } = useTranslate()
	const versionsQuery = useQuery(trpc.policies.ListVersions.queryOptions({ partnerId }))
	const versions = versionsQuery.data ?? []
	const current = versions[0]

	return (
		<>
			<section className='flex flex-col gap-2'>
				<SectionTitle
					title={t('policies.effectiveTitle')}
					hint={t('policies.effectiveHint')}
					aside={
						current && (
							<span className='text-sm text-muted-foreground'>
								{t('policies.versionLabel', { version: String(current.version) })}
							</span>
						)
					}
				/>
				<EffectiveSettings partnerId={partnerId} hasPolicy={Boolean(current)} />
			</section>

			<section className='flex flex-col gap-2'>
				<SectionTitle title={t('policies.historyTitle')} count={versions.length} />
				<VersionTable versions={versions} loading={versionsQuery.isLoading} />
			</section>

			<PolicySheet
				partnerId={partnerId}
				current={current}
				open={editing}
				onOpenChange={onEditingChange}
				onSaved={() => versionsQuery.refetch()}
			/>
		</>
	)
}

/** The settings in force — the server evaluator's answer — one field to a cell. */
function EffectiveSettings({ partnerId, hasPolicy }: { partnerId: string; hasPolicy: boolean }) {
	const { t } = useTranslate()
	const words = useWords()
	const effectiveQuery = useQuery({
		...trpc.policies.EffectivePermissions.queryOptions({ partnerId }),
		enabled: hasPolicy,
		retry: false,
	})

	if (!hasPolicy) {
		return <p className='text-sm text-muted-foreground'>{t('policies.effectiveNeedPolicy')}</p>
	}
	if (effectiveQuery.isError) {
		return <p className='text-sm text-destructive'>{effectiveQuery.error.message}</p>
	}
	const e: Effective | undefined = effectiveQuery.data
	if (!e) return <p className='text-sm text-muted-foreground'>{t('policies.loading')}</p>
	return (
		<dl className='grid grid-cols-2 gap-x-8 gap-y-4 rounded-md border p-4 lg:grid-cols-4' data-testid='policy-effective'>
			<Fact label={t('policies.colPays')}>{words.pays(e.recipientPays)}</Fact>
			<Fact label={t('policies.colIdentification')}>{words.identification(e.identification, e.documentType)}</Fact>
			<Fact label={t('policies.colTrust')}>{words.trust(e.trustSetting, e.weightTolerancePercent)}</Fact>
			<Fact label={t('policies.colTariff')}>{words.tariff(e.tariffPerKg)}</Fact>
			<Fact label={t('policies.colUnpaid')}>{words.days(e.unpaidStorageDays)}</Fact>
			<Fact label={t('policies.colPaid')}>{words.days(e.paidStorageDays)}</Fact>
			<Fact label={t('policies.colAnswer')}>{words.days(e.answerPeriodDays)}</Fact>
		</dl>
	)
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className='flex flex-col gap-0.5'>
			<dt className='text-xs text-muted-foreground'>{label}</dt>
			<dd className='text-sm font-medium'>{children}</dd>
		</div>
	)
}

function VersionTable({ versions, loading }: { versions: PolicyVersion[]; loading: boolean }) {
	const { t } = useTranslate()
	const words = useWords()
	// Headers may wrap: nine settings side by side must fit the page.
	const head = 'whitespace-normal align-bottom'
	const columns: ColumnDef<PolicyVersion>[] = [
		{
			id: 'version',
			header: t('policies.colVersion'),
			meta: { headClassName: head },
			cell: ({ row }) => <span className='font-semibold tabular-nums'>{row.original.version}</span>,
		},
		{
			id: 'pays',
			header: t('policies.colPays'),
			meta: { headClassName: head },
			cell: ({ row }) => words.pays(row.original.recipientPays),
		},
		{
			id: 'identification',
			header: t('policies.colIdentification'),
			meta: { headClassName: head },
			cell: ({ row }) => words.identification(row.original.identification, row.original.documentType),
		},
		{
			id: 'trust',
			header: t('policies.colTrust'),
			meta: { headClassName: head },
			cell: ({ row }) => words.trust(row.original.trustSetting, row.original.weightTolerancePercent),
		},
		{
			id: 'unpaid',
			header: t('policies.colUnpaid'),
			meta: { headClassName: head },
			cell: ({ row }) => words.days(row.original.unpaidStorageDays),
		},
		{
			id: 'paid',
			header: t('policies.colPaid'),
			meta: { headClassName: head },
			cell: ({ row }) => words.days(row.original.paidStorageDays),
		},
		{
			id: 'answer',
			header: t('policies.colAnswer'),
			meta: { headClassName: head },
			cell: ({ row }) => words.days(row.original.answerPeriodDays),
		},
		{
			id: 'tariff',
			header: t('policies.colTariff'),
			meta: { headClassName: `${head} text-right`, cellClassName: 'text-right tabular-nums' },
			cell: ({ row }) => words.tariff(row.original.tariffPerKg),
		},
		{
			id: 'created',
			header: t('policies.colCreated'),
			meta: { headClassName: head },
			cell: ({ row }) => <span className='text-muted-foreground'>{formatDate(row.original.createdAt)}</span>,
		},
	]
	const { table } = useDataTable({
		data: versions,
		columns,
		rowCount: versions.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (v) => v._id,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})
	if (loading) return <p className='text-sm text-muted-foreground'>{t('policies.loading')}</p>
	return (
		<DataTable
			table={table}
			withPagination={false}
			emptyText={t('policies.noVersionYet')}
			row={{ attributes: (v) => ({ 'data-testid': `policy-version-${v.version}` }) }}
		/>
	)
}

/** A label with its explanation behind an «i». */
function FieldLabel({ htmlFor, hint, children }: { htmlFor?: string; hint?: string; children: ReactNode }) {
	return (
		<div className='flex items-center gap-1.5'>
			<Label htmlFor={htmlFor}>{children}</Label>
			{hint && <Hint>{hint}</Hint>}
		</div>
	)
}

/** A new version of the Policy, written from the current one; saving makes it the current one. */
function PolicySheet({
	partnerId,
	current,
	open,
	onOpenChange,
	onSaved,
}: {
	partnerId: string
	current: PolicyVersion | undefined
	open: boolean
	onOpenChange: (open: boolean) => void
	onSaved: () => Promise<unknown>
}) {
	const { t } = useTranslate()
	const seed = (version: PolicyVersion | undefined) => (version ? settingsFromVersion(version) : DEFAULT_SETTINGS)
	const [settings, setSettings] = useState<PolicyForm>(() => seed(current))

	// The form starts from the current version each time the panel opens — during
	// render, not in an effect, so no stale form is ever painted.
	const [wasOpen, setWasOpen] = useState(open)
	if (open !== wasOpen) {
		setWasOpen(open)
		if (open) setSettings(seed(current))
	}

	const saveMutation = useMutation({
		...trpc.policies.Save.mutationOptions(),
		onSuccess: async () => {
			await onSaved()
			onOpenChange(false)
			toast.success(t('policies.saved'))
		},
		onError: (error) => toast.error(error.message),
	})

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		if (settings.tariffPerKg === null) return
		saveMutation.mutate({
			partnerId,
			// Compare-and-set: state the version we believe is current, so a
			// concurrent editor cannot silently overwrite our change.
			expectedVersion: current?.version ?? 0,
			policy: { ...settings, tariffPerKg: settings.tariffPerKg },
		})
	}

	const canSave =
		!saveMutation.isPending &&
		!(settings.identification === 'by_document' && !settings.documentType) &&
		settings.tariffPerKg !== null &&
		Number.isInteger(settings.tariffPerKg) &&
		settings.tariffPerKg >= 0

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent className='data-[side=right]:sm:max-w-xl' data-testid='policy-sheet'>
				<form className='flex min-h-0 flex-1 flex-col' onSubmit={handleSubmit}>
					<SheetHeader>
						<SheetTitle className='text-lg'>{t('policies.newVersion')}</SheetTitle>
						<SheetDescription>
							{current
								? t('policies.newVersionOf', {
										version: String(current.version),
										next: String(current.version + 1),
									})
								: t('policies.firstVersion')}
						</SheetDescription>
					</SheetHeader>
					<div className='grid flex-1 content-start gap-x-4 gap-y-5 overflow-y-auto px-4 py-2 sm:grid-cols-2'>
						{/* Does the Recipient pay (ADR-0032): "no" — nothing is owed, whatever
						    the Manifest line says. */}
						<div className='grid gap-1.5'>
							<FieldLabel>{t('policies.recipientPays')}</FieldLabel>
							<Select
								value={settings.recipientPays ? 'yes' : 'no'}
								onValueChange={(v) => setSettings((s) => ({ ...s, recipientPays: v === 'yes' }))}
							>
								<SelectTrigger className='w-full' data-testid='policy-recipient-pays'>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value='yes'>{t('policies.recipientPaysYes')}</SelectItem>
									<SelectItem value='no'>{t('policies.recipientPaysNo')}</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{/* How the hub takes the batch in (ADR-0026): by boxes, or per piece. */}
						<div className='grid gap-1.5'>
							<FieldLabel>{t('policies.trustSetting')}</FieldLabel>
							<Select
								value={settings.trustSetting}
								onValueChange={(v) => setSettings((s) => ({ ...s, trustSetting: v as TrustSetting }))}
							>
								<SelectTrigger className='w-full' data-testid='policy-trust-setting'>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{TRUST_SETTINGS.map((v) => (
										<SelectItem key={v} value={v}>
											{t(`policies.trust_${v}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className='grid gap-1.5'>
							<FieldLabel htmlFor='weightTolerancePercent' hint={t('policies.weightToleranceHint')}>
								{t('policies.weightTolerance')}
							</FieldLabel>
							<Input
								id='weightTolerancePercent'
								data-testid='policy-weight-tolerance'
								type='number'
								min={0}
								max={100}
								step='0.5'
								value={settings.weightTolerancePercent}
								onChange={(e) => setSettings((s) => ({ ...s, weightTolerancePercent: Number(e.target.value) }))}
							/>
						</div>

						{/* TezGo's tariff per kg (ADR-0034): whole som for 1 kg, required — the
						    Per-kg fee is it times the hub's weight, at the version pinned at the hub. */}
						<div className='grid gap-1.5'>
							<FieldLabel htmlFor='tariffPerKg' hint={t('policies.tariffPerKgHint')}>
								{t('policies.tariffPerKg')}
							</FieldLabel>
							<Input
								id='tariffPerKg'
								data-testid='policy-tariff-per-kg'
								type='number'
								min={0}
								step={1}
								placeholder={t('policies.tariffNotSet')}
								value={settings.tariffPerKg ?? ''}
								onChange={(e) =>
									setSettings((s) => ({
										...s,
										tariffPerKg: e.target.value === '' ? null : Number(e.target.value),
									}))
								}
							/>
						</div>

						{/* How the Recipient is identified (ADR-0031): by code, or by document —
						    then its type is required, and chosen with no default. */}
						<div className='grid gap-1.5'>
							<FieldLabel hint={t('policies.identificationHint')}>{t('policies.identification')}</FieldLabel>
							<Select
								value={settings.identification}
								onValueChange={(v) =>
									setSettings((s) => ({
										...s,
										identification: v as Identification,
										documentType: v === 'by_document' ? s.documentType : null,
									}))
								}
							>
								<SelectTrigger className='w-full' data-testid='policy-identification'>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{IDENTIFICATIONS.map((v) => (
										<SelectItem key={v} value={v}>
											{t(`policies.identification_${v}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{settings.identification === 'by_document' ? (
							<div className='grid gap-1.5'>
								<FieldLabel>{t('policies.documentType')}</FieldLabel>
								<Select
									value={settings.documentType ?? undefined}
									onValueChange={(v) => setSettings((s) => ({ ...s, documentType: v as DocumentType }))}
								>
									<SelectTrigger className='w-full' data-testid='policy-document-type'>
										<SelectValue placeholder={t('policies.documentType')} />
									</SelectTrigger>
									<SelectContent>
										{DOCUMENT_TYPES.map((v) => (
											<SelectItem key={v} value={v}>
												{t(`policies.documentType_${v}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						) : (
							<div className='hidden sm:block' />
						)}

						{/* The two storage periods and the answer period (ADR-0033): both periods
						    count from the shelf; an empty paid period never expires. */}
						<div className='grid gap-1.5'>
							<FieldLabel htmlFor='unpaidStorageDays'>{t('policies.unpaidStorageDays')}</FieldLabel>
							<Input
								id='unpaidStorageDays'
								data-testid='policy-unpaid-days'
								type='number'
								min={1}
								max={365}
								value={settings.unpaidStorageDays}
								onChange={(e) => setSettings((s) => ({ ...s, unpaidStorageDays: Number(e.target.value) }))}
							/>
						</div>

						<div className='grid gap-1.5'>
							<FieldLabel htmlFor='paidStorageDays' hint={t('policies.paidStorageDaysHint')}>
								{t('policies.paidStorageDays')}
							</FieldLabel>
							<Input
								id='paidStorageDays'
								data-testid='policy-paid-days'
								type='number'
								min={1}
								max={365}
								placeholder={t('policies.neverExpires')}
								value={settings.paidStorageDays ?? ''}
								onChange={(e) =>
									setSettings((s) => ({
										...s,
										paidStorageDays: e.target.value === '' ? null : Number(e.target.value),
									}))
								}
							/>
						</div>

						<div className='grid gap-1.5'>
							<FieldLabel htmlFor='answerPeriodDays' hint={t('policies.answerPeriodDaysHint')}>
								{t('policies.answerPeriodDays')}
							</FieldLabel>
							<Input
								id='answerPeriodDays'
								data-testid='policy-answer-days'
								type='number'
								min={1}
								max={90}
								value={settings.answerPeriodDays}
								onChange={(e) => setSettings((s) => ({ ...s, answerPeriodDays: Number(e.target.value) }))}
							/>
						</div>
					</div>
					<SheetFooter className='flex-row justify-end'>
						<Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
							{t('policies.cancel')}
						</Button>
						<Button data-testid='policy-save' disabled={!canSave}>
							{t('policies.save')}
						</Button>
					</SheetFooter>
				</form>
			</SheetContent>
		</Sheet>
	)
}
